import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/scrollbar.css";

const EyeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeSlashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a18.66 18.66 0 0 1 5.06-5.94M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a18.66 18.66 0 0 1-2.06 2.94M14.12 14.12a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

// Shows the first 3 characters and masks the rest so sensitive contact info isn't visible by default.
const maskValue = (value) => {
  const str = String(value || "");
  if (str.length <= 3) return str;
  return str.slice(0, 3) + "*".repeat(str.length - 3);
};

function MainMaster() {
  const [currentPage, setCurrentPage] = useState(1);
  const [showAll, setShowAll] = useState(false);
  const [downlineData, setDownlineData] = useState([]);
  const [commissionData, setCommissionData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("downline");
  const [hasVVP, setHasVVP] = useState(false);
  const [revealedCells, setRevealedCells] = useState({});
  const [dateFrom, setDateFrom] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    // Adjust for timezone offset to ensure local date is used
    const offset = date.getTimezoneOffset() * 60000;
    const localDate = new Date(date.getTime() - offset);
    return localDate.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => {
    const date = new Date();
    // Adjust for timezone offset to ensure local date is used
    const offset = date.getTimezoneOffset() * 60000;
    const localDate = new Date(date.getTime() - offset);
    return localDate.toISOString().split('T')[0];
  });
  const navigate = useNavigate();

  const custId = localStorage.getItem("partnerCustId");
  const userName = localStorage.getItem("partnerUserName") || "";
  // console.log("Current Partner CustId:", custId);

  useEffect(() => {
    if (!custId) {
      navigate("/partner");
      return;
    }

    fetchDownlineData();
    fetchProfile();
  }, [custId]);

  // Only fetch commission data when switching to the commission tab
  useEffect(() => {
    if (custId && activeTab === "commission") {
      fetchCommissionData();
    }
  }, [activeTab]);

  // Pull the logged-in partner profile so we know whether they hold a VVP (IC) code.
  // Only VVP holders may see the higher commission tier.
  const fetchProfile = async () => {
    try {
      const profileResponse = await fetch(
        "https://xianhefairy.com/LotusFairy_API/api/User/GetProfile",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            UserName: userName,
            ID: parseInt(custId || "0", 10),
            LangId: parseInt(localStorage.getItem("languageId") || "2224", 10) || 2224,
            UserToken: "",
            Token: {
              AccessToken: "xAfhn90xflfHfd.fjldfj@fdhLXHCHchlhsdflsdfhawWFRwzxxp",
            },
          }),
        }
      );
      const profileResult = await profileResponse.json();
      const profile = profileResult?.Profiles?.[0];
      // console.log("Partner profile:", profile);
      setHasVVP(Boolean(profile?.VVP_CODE && String(profile.VVP_CODE).trim()));
    } catch (err) {
      console.error("Profile fetch error:", err);
      setHasVVP(false);
    }
  };

  const fetchDownlineData = async () => {
    setLoading(true);
    setError("");

    try {
      const downlineResponse = await fetch(
        `https://xianhefairy.com/LotusFairy_API/api/User/GetMemberDownline?CustId=${custId}`
      );
      const downlineResult = await downlineResponse.json();
      
      // console.log("Downline API Response:", downlineResult);
      // console.log("Is Array:", Array.isArray(downlineResult));

      // API returns array directly when successful
      if (Array.isArray(downlineResult)) {
        // console.log("Setting downline data, length:", downlineResult.length);
        setDownlineData(downlineResult);
      } else if (downlineResult.successful) {
        setDownlineData(downlineResult.data || []);
      } else {
        setError("Failed to load downline data");
        // console.log("API returned unexpected format:", downlineResult);
      }
    } catch (err) {
      setError("Error loading downline data: " + err.message);
      console.error("Downline fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCommissionData = async () => {
    setLoading(true);
    setError("");

    // console.log("Fetching commission data for CustId:", custId, "from", dateFrom, "to", dateTo);

    try {
      const commissionResponse = await fetch(
        `https://xianhefairy.com/LotusFairy_API/api/User/GetPartnerComm?CustId=${custId}&DateFrom=${dateFrom}&DateTo=${dateTo}`
      );
      console.log("Commission response status:", commissionResponse.url);
      const commissionResult = await commissionResponse.json();
      // console.log("Commission result:", commissionResult);

      // API returns array directly, not wrapped in {successful, data}
      if (Array.isArray(commissionResult)) {
        // console.log("Setting commission data:", commissionResult);
        setCommissionData(commissionResult);
      } else if (commissionResult.successful) {
        setCommissionData(commissionResult.data || []);
      } else {
        setError("Failed to load commission data");
        // console.log("API returned unsuccessful:", commissionResult);
      }
    } catch (err) {
      setError("Error loading commission data: " + err.message);
      console.error("Commission fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const toggleReveal = (key) => {
    setRevealedCells((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleLogout = () => {
    localStorage.removeItem("partnerCustId");
    localStorage.removeItem("partnerUserName");
    navigate("/partner");
  };

  const handleDateSearch = () => {
    if (activeTab === "commission") {
      setCurrentPage(1); // Reset to first page
      fetchCommissionData();
    }
  };


  // Partners without a VVP code do not earn the 10% tier, so drop that group entirely.
  const visibleCommissionData = hasVVP
    ? commissionData
    : commissionData.filter((group) => {
        const percent = Number(group?.CommPercent);
        if (Number.isFinite(percent) && percent > 0) return percent < 10;
        return !/10\s*%/.test(String(group?.CommDesc || ""));
      });

  const currentData = activeTab === "downline" ? downlineData : visibleCommissionData;

  
  const totalRows = currentData.length;
  const rowsPerPage = showAll ? totalRows : 15;
  const totalPages = Math.ceil(totalRows / rowsPerPage) || 1;
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = showAll ? totalRows : Math.min(startIndex + rowsPerPage, totalRows);
  const displayData = currentData.slice(startIndex, endIndex);

    // console.log("Current Partner CustId:", custId);
  // console.log("Current Data:", currentData);
  // console.log("Display Data:", displayData);
  const tableStyles = {
    container: {
      padding: "20px",
      backgroundColor: "#272725",
      minHeight: "100vh",
      color: "#DFBD69"
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "20px"
    },
    title: {
      fontSize: "24px",
      fontWeight: "600",
      color: "#DFBD69"
    },
    logoutButton: {
      padding: "8px 16px",
      backgroundColor: "#DFBD69",
      color: "#272725",
      border: "none",
      borderRadius: "4px",
      cursor: "pointer",
      fontSize: "14px",
      fontWeight: "500"
    },
    tabs: {
      display: "flex",
      gap: "10px",
      marginBottom: "20px"
    },
    tab: {
      padding: "10px 20px",
      backgroundColor: "#1a1a18",
      color: "#DFBD69",
      border: "1px solid #DFBD69",
      borderRadius: "4px",
      cursor: "pointer",
      fontSize: "14px",
      fontWeight: "500"
    },
    activeTab: {
      padding: "10px 20px",
      backgroundColor: "#DFBD69",
      color: "#272725",
      border: "1px solid #DFBD69",
      borderRadius: "4px",
      cursor: "pointer",
      fontSize: "14px",
      fontWeight: "600"
    },
    dateFilter: {
      display: "flex",
      gap: "10px",
      alignItems: "center",
      marginBottom: "15px",
      padding: "12px",
      backgroundColor: "#1a1a18",
      borderRadius: "4px"
    },
    dateLabel: {
      fontSize: "14px",
      color: "#DFBD69",
      fontWeight: "500"
    },
    dateInput: {
      padding: "6px 10px",
      backgroundColor: "#272725",
      color: "#DFBD69",
      border: "1px solid #DFBD69",
      borderRadius: "3px",
      fontSize: "13px",
      outline: "none",
    },
    searchButton: {
      padding: "6px 16px",
      backgroundColor: "#DFBD69",
      color: "#272725",
      border: "none",
      borderRadius: "4px",
      cursor: "pointer",
      fontSize: "13px",
      fontWeight: "500"
    },
    navigation: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      marginBottom: "10px",
      padding: "8px",
      backgroundColor: "#1a1a18",
      borderRadius: "4px"
    },
    navButton: {
      width: "28px",
      height: "28px",
      backgroundColor: "#DFBD69",
      border: "1px solid #888",
      borderRadius: "3px",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "12px",
      color: "#272725",
      fontWeight: "bold"
    },
    pageSelect: {
      padding: "4px 8px",
      backgroundColor: "white",
      border: "1px solid #888",
      borderRadius: "3px",
      color: "#272725",
      fontSize: "13px"
    },
    checkbox: {
      marginLeft: "12px",
      fontSize: "13px",
      color: "#DFBD69",
      display: "flex",
      alignItems: "center",
      gap: "4px"
    },
    pageInfo: {
      marginLeft: "auto",
      fontSize: "13px",
      color: "#DFBD69"
    },
    tableWrapper: {
      overflowX: "auto",
      overflowY: "auto",
      maxHeight: "calc(100vh - 280px)",
      backgroundColor: "#1a1a18",
      borderRadius: "4px",
      // Add custom scrollbar styles
      scrollbarWidth: "thin",
      scrollbarColor: "#DFBD69 #151517"
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      fontSize: "13px"
    },
    th: {
      backgroundColor: "#3a3a38",
      color: "#DFBD69",
      padding: "10px 8px",
      textAlign: "left",
      borderBottom: "2px solid #DFBD69",
      fontWeight: "600",
      position: "sticky",
      top: 0,
      zIndex: 10
    },
    td: {
      padding: "8px",
      borderBottom: "1px solid #444",
      color: "#DFBD69"
    },
    evenRow: {
      backgroundColor: "#242422"
    },
    oddRow: {
      backgroundColor: "#1a1a18"
    },
    loading: {
      textAlign: "center",
      padding: "40px",
      fontSize: "16px",
      color: "#DFBD69"
    },
    error: {
      textAlign: "center",
      padding: "20px",
      fontSize: "14px",
      color: "#ff6b6b",
      backgroundColor: "#1a1a18",
      borderRadius: "4px",
      marginBottom: "20px"
    },
    maskedCell: {
      display: "flex",
      alignItems: "center",
      gap: "6px"
    },
    eyeButton: {
      background: "none",
      border: "none",
      padding: 0,
      cursor: "pointer",
      color: "#DFBD69",
      display: "flex",
      alignItems: "center",
      flexShrink: 0
    }
  };

  return (
    <div style={tableStyles.container}>
      <div style={tableStyles.header}>
        <h1 style={tableStyles.title}>Partner Dashboard</h1>
        <div style={{ display: "flex", gap: "10px" }}>
          <button
            style={tableStyles.tab}
            onClick={() => navigate("/account", { state: { setactiveTab: "commission" } })}
          >
            Back to Account
          </button>
          <button style={tableStyles.logoutButton} onClick={handleLogout}>
            Logout
          </button>
        </div>
      </div>

      {error && <div style={tableStyles.error}>{error}</div>}

      <div style={tableStyles.tabs}>
        <button
          style={activeTab === "downline" ? tableStyles.activeTab : tableStyles.tab}
          onClick={() => {
            setActiveTab("downline");
            setCurrentPage(1);
          }}
        >
          Member Downline
        </button>
        <button
          style={activeTab === "commission" ? tableStyles.activeTab : tableStyles.tab}
          onClick={() => {
            setActiveTab("commission");
            setCurrentPage(1);
          }}
        >
          Rewards
        </button>
      </div>

      {activeTab === "commission" && (
        <div style={tableStyles.dateFilter}>
          <span style={tableStyles.dateLabel}>Date From:</span>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            style={tableStyles.dateInput}
          />
          <span style={tableStyles.dateLabel}>Date To:</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            style={tableStyles.dateInput}
          />
          <button style={tableStyles.searchButton} onClick={handleDateSearch}>
            Search
          </button>
        </div>
      )}

      <div style={tableStyles.navigation}>
        <button
          style={tableStyles.navButton}
          onClick={() => setCurrentPage(1)}
          disabled={currentPage === 1}
        >
          ◄◄
        </button>
        <button
          style={tableStyles.navButton}
          onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
        >
          ◄
        </button>
        <select
          style={tableStyles.pageSelect}
          value={currentPage}
          onChange={(e) => setCurrentPage(Number(e.target.value))}
        >
          {[...Array(totalPages)].map((_, i) => (
            <option key={i + 1} value={i + 1}>
              {i + 1}
            </option>
          ))}
        </select>
        <button
          style={tableStyles.navButton}
          onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
        >
          ►
        </button>
        <button
          style={tableStyles.navButton}
          onClick={() => setCurrentPage(totalPages)}
          disabled={currentPage === totalPages}
        >
          ►►
        </button>
        <label style={tableStyles.checkbox}>
          <input
            type="checkbox"
            checked={showAll}
            onChange={(e) => setShowAll(e.target.checked)}
          />
          Show All
        </label>
        <span style={tableStyles.pageInfo}>
          Page {currentPage} of {totalPages} - Total Rows: {totalRows}
        </span>
      </div>

      <div style={tableStyles.tableWrapper}>
        {loading ? (
          <div style={tableStyles.loading}>Loading data...</div>
        ) : (
          <table style={tableStyles.table}>
            <thead>
              <tr>
                {activeTab === "downline" ? (
                  <>
                    <th style={tableStyles.th}>SN</th>
                    <th style={tableStyles.th}>Code</th>
                    <th style={tableStyles.th}>Team</th>
                    <th style={tableStyles.th}>Member</th>
                    {/* <th style={tableStyles.th}>Referral Code</th> */}
                    {/* <th style={tableStyles.th}>D.O.B.</th> */}
                    <th style={tableStyles.th}>Mobile No.</th>
                    <th style={tableStyles.th}>Email</th>
                    <th style={tableStyles.th}>IC</th>
                    <th style={tableStyles.th}>AP</th>
                    <th style={{...tableStyles.th, textAlign: "right"}}>Gem</th>
                    <th style={{...tableStyles.th, textAlign: "right"}}>Commission</th>
                  </>
                ) : (
                  <>
                    <th style={tableStyles.th}>SN</th>
                    <th style={tableStyles.th}>Date</th>
                    <th style={tableStyles.th}>Member</th>
                    <th style={tableStyles.th}>Upline</th>
                    <th style={{...tableStyles.th, textAlign: "right"}}>Purchased Amount</th>
                    <th style={{...tableStyles.th, textAlign: "right"}}>Rewards</th>
                    <th style={tableStyles.th}>Item</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {displayData.length === 0 ? (
                <tr>
                  <td colSpan={activeTab === "downline" ? 12 : 7} style={{...tableStyles.td, textAlign: "center", padding: "20px"}}>
                    No data available
                  </td>
                </tr>
              ) : activeTab === "downline" ? (
                displayData.map((row, index) => (
                  <tr key={index} style={index % 2 === 0 ? tableStyles.evenRow : tableStyles.oddRow}>
                    <td style={tableStyles.td}>{startIndex + index + 1}</td>
                    <td style={tableStyles.td}>{row.CODE || row.Code || row.code || ""}</td>
                    <td style={tableStyles.td}>{row.TEAM || row.Team || row.team || ""}</td>
                    <td style={tableStyles.td}>
                      {[row.FIRST_NAME, row.LAST_NAME].filter(Boolean).join(" ") ||
                        row.Member ||
                        row.memberName ||
                        ""}
                    </td>
                    {/* <td style={tableStyles.td}>{row.UPLINE || row.ReferralCode || row.referralCode || ""}</td> */}
                    {/* <td style={tableStyles.td}>{row.DOB || row.dob || ""}</td> */}
                    <td style={tableStyles.td}>
                      <div style={tableStyles.maskedCell}>
                        <span>
                          {revealedCells[`${index}-mobile`]
                            ? row.MOBILE || row.Mobile || row.mobile || ""
                            : maskValue(row.MOBILE || row.Mobile || row.mobile || "")}
                        </span>
                        {/* <button
                          type="button"
                          style={tableStyles.eyeButton}
                          onClick={() => toggleReveal(`${index}-mobile`)}
                          aria-label={revealedCells[`${index}-mobile`] ? "Hide mobile number" : "Show mobile number"}
                        >
                          {revealedCells[`${index}-mobile`] ? <EyeSlashIcon /> : <EyeIcon />}
                        </button> */}
                      </div>
                    </td>
                    <td style={tableStyles.td}>
                      <div style={tableStyles.maskedCell}>
                        <span>
                          {revealedCells[`${index}-email`]
                            ? row.EMAIL || row.Email || row.email || ""
                            : maskValue(row.EMAIL || row.Email || row.email || "")}
                        </span>
                        {/* <button
                          type="button"
                          style={tableStyles.eyeButton}
                          onClick={() => toggleReveal(`${index}-email`)}
                          aria-label={revealedCells[`${index}-email`] ? "Hide email" : "Show email"}
                        >
                          {revealedCells[`${index}-email`] ? <EyeSlashIcon /> : <EyeIcon />}
                        </button> */}
                      </div>
                    </td>
                    <td style={tableStyles.td}>{row.VVP_CODE || row.IC || row.ic || ""}</td>
                    <td style={tableStyles.td}>{row.VP_CODE || row.AP || row.ap || ""}</td>
                    <td style={{...tableStyles.td, textAlign: "right"}}>
                      {(row.COINS || row.Gem || row.gem || 0).toFixed(2)}
                    </td>
                    <td style={{...tableStyles.td, textAlign: "right"}}>
                      {(row.CHARM || row.Commission || row.commission || 0).toFixed(2)}
                    </td>
                  </tr>
                ))
              ) : (
                // Render commission data with groups
                displayData.map((commGroup, groupIndex) => (
                  <React.Fragment key={groupIndex}>
                    <tr style={{backgroundColor: "#3a3a38"}}>
                      <td colSpan={7} style={{...tableStyles.td, fontWeight: "bold", padding: "12px 8px"}}>
                        {commGroup.CommDesc}
                      </td>
                    </tr>
                    {commGroup.Data && commGroup.Data.map((row, rowIndex) => (
                      <tr key={`${groupIndex}-${rowIndex}`} style={rowIndex % 2 === 0 ? tableStyles.evenRow : tableStyles.oddRow}>
                        <td style={tableStyles.td}>{rowIndex + 1}</td>
                        <td style={tableStyles.td}>{row.Date || ""}</td>
                        <td style={tableStyles.td}>{row.FullName || row.fname || ""}</td>
                        <td style={tableStyles.td}>{row.Iname || ""}</td>
                        <td style={{...tableStyles.td, textAlign: "right"}}>
                          {(row.TotalAmount || 0).toFixed(2)}
                        </td>
                        <td style={{...tableStyles.td, textAlign: "right"}}>
                          {(row.Commission || 0).toFixed(2)}
                        </td>
                        <td style={tableStyles.td}>{row.ItemDesc || ""}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default MainMaster;